import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type {
  TaskRecord,
  TaskStatusUpdatePayload,
  TaskDetailRecord,
  ScheduleDetailRecord,
  TaskTypeRecord,
  SliderTimerState,
} from "@ServerTypes/tasks/tasks.schema";

// Slice to store the list of tasks returned by APIs
const taskDataSlice = createSlice({
  name: "taskData",
  initialState: [] as TaskRecord[],
  reducers: {
    setTaskData: (_state: TaskRecord[], action: PayloadAction<TaskRecord[]>) => action.payload,
    updateTaskData: (state: TaskRecord[], action: PayloadAction<TaskStatusUpdatePayload>) => {
      const tempState = [...state];
      const taskId = action.payload.id;
      if (tempState[taskId] && tempState[taskId].TASKS_STATUS) {
        tempState[taskId].TASKS_STATUS!.statusId = action.payload.status;
      }
      return tempState;
    },
    removeTaskData: (state: TaskRecord[]) => {
      const localState = [...state];
      return localState;
    },
  },
});

// Slice to store task detail information
const taskDetailsSlice = createSlice({
  name: "taskDetails",
  initialState: {} as TaskDetailRecord,
  reducers: {
    setTaskDetails: (state: TaskDetailRecord, action: PayloadAction<Partial<TaskDetailRecord>>) => ({
      ...state,
      ...action.payload,
    }),
  },
});

// Slice to store gantt schedule information
const ganttDetailsSlice = createSlice({
  name: "ganttDetails",
  initialState: {} as ScheduleDetailRecord,
  reducers: {
    setScheduleDetails: (_state: ScheduleDetailRecord, action: PayloadAction<ScheduleDetailRecord>) => ({
      ...action.payload,
    }),
    deleteScheduleDetails: () => ({}) as ScheduleDetailRecord,
  },
});

// Slice to store schedule error code (false = no error); drives the no-access page
const scheduleErrorSlice = createSlice({
  name: "scheduleError",
  initialState: false as boolean | number,
  reducers: {
    setScheduleError: (_state, action: PayloadAction<boolean | number>) => action.payload,
  },
});

// Slice to store task types.
//
// KNOWN SHAPE MISMATCH, preserved verbatim: the one dispatcher (`getTaskTypes` in actions/task.ts)
// passes the response's `TaskTypeRecord[]`, and spreading an array into this object keys each
// record by its index ("0", "1", …) — the values are never `TaskTypeRecord[]`. Nothing selects
// `state.TaskTypes`, so the state is typed for what the reducer actually produces instead of
// being asserted back into the array-valued shape it never holds.
const taskTypesSlice = createSlice({
  name: "taskTypes",
  initialState: {} as Record<string, unknown>,
  reducers: {
    setTaskTypes: (state: Record<string, unknown>, action: PayloadAction<TaskTypeRecord[]>) => ({
      ...state,
      ...action.payload,
    }),
  },
});

// Slice used for slider timer data
const sliderTimerSlice = createSlice({
  name: "sliderTimer",
  initialState: {} as SliderTimerState,
  reducers: {
    setScheduleSliderTime: (state: SliderTimerState, action: PayloadAction<Partial<SliderTimerState>>) => ({
      ...state,
      ...action.payload,
    }),
  },
});

// Selection set elements: taskId -> array of elementIds
type SelectionSetElementsState = Record<string, string[]>;

// Slice for selection set elements associated with tasks
const selectionSetElementsSlice = createSlice({
  name: "selectionSetTaskElements",
  initialState: {} as SelectionSetElementsState,
  reducers: {
    setSelectionSetTaskElements: (
      state: SelectionSetElementsState,
      action: PayloadAction<SelectionSetElementsState>,
    ) => ({
      ...state,
      ...action.payload,
    }),
  },
});

export type SimulationOverlayPosition = "topLeft" | "topRight" | "bottomLeft" | "bottomRight";

export interface SimulationCostMetricsVisibility {
  plannedValue: boolean;
  actualCost: boolean;
  earnedValue: boolean;
}

export interface SimulationDisplaySettingsPayload {
  displayPosition: SimulationOverlayPosition;
  costMetricsVisibility: SimulationCostMetricsVisibility;
}

export interface SimulationMetricsState extends SimulationDisplaySettingsPayload {
  plannedValue: number;
  actualCost: number;
  earnedValue: number;
}

export const DEFAULT_SIMULATION_OVERLAY_POSITION: SimulationOverlayPosition = "topLeft";
export const DEFAULT_SIMULATION_COST_METRICS: SimulationCostMetricsVisibility = {
  plannedValue: true,
  actualCost: false,
  earnedValue: true,
};

const simulationMetricsInitialState: SimulationMetricsState = {
  plannedValue: 0,
  actualCost: 0,
  earnedValue: 0,
  displayPosition: DEFAULT_SIMULATION_OVERLAY_POSITION,
  costMetricsVisibility: DEFAULT_SIMULATION_COST_METRICS,
};

const simulationMetricsSlice = createSlice({
  name: "simulationMetrics",
  initialState: simulationMetricsInitialState,
  reducers: {
    setTaskCost: (state: SimulationMetricsState, action: PayloadAction<number>) => {
      state.plannedValue = action.payload;
    },
    setTaskActualCost: (state: SimulationMetricsState, action: PayloadAction<number>) => {
      state.actualCost = action.payload;
    },
    setTaskEarnedValue: (state: SimulationMetricsState, action: PayloadAction<number>) => {
      state.earnedValue = action.payload;
    },
    setSimulationDisplaySettings: (
      state: SimulationMetricsState,
      action: PayloadAction<SimulationDisplaySettingsPayload>,
    ) => {
      state.displayPosition = action.payload.displayPosition;
      state.costMetricsVisibility = action.payload.costMetricsVisibility;
    },
  },
});

// `setTaskData` payload is `TaskRecord[]` (array) — exported raw.
// `removeTaskData` has no payload — exported raw.
// `setTaskTypes` payload is `TaskTypeRecord[]` (array) — exported raw.
// `setSelectionSetTaskElements` payload is a `Record<string, string[]>`
// (index-signature) — `Exact<>` doesn't enforce useful constraints.
// `deleteScheduleDetails` has no payload — exported raw.
// `setTaskCost`/`setTaskActualCost`/`setTaskEarnedValue` payloads are primitives
// (number) — exported raw.
export const { setTaskData, removeTaskData } = taskDataSlice.actions;
export const { deleteScheduleDetails } = ganttDetailsSlice.actions;
export const { setScheduleError } = scheduleErrorSlice.actions;
export const { setTaskTypes } = taskTypesSlice.actions;
export const { setSelectionSetTaskElements } = selectionSetElementsSlice.actions;
export const { setTaskCost, setTaskActualCost, setTaskEarnedValue } = simulationMetricsSlice.actions;
const { setSimulationDisplaySettings: _setSimulationDisplaySettingsRaw } = simulationMetricsSlice.actions;

// `setTaskDetails` and `setScheduleDetails` are intentionally exported raw
// (NOT wrapped with `Exact<>`). Consumers in actions/task.ts pass untyped
// `Record<string, any>` / `{ [x: string]: unknown; }` payloads which fail
// Exact's enforcement.
export const { setTaskDetails } = taskDetailsSlice.actions;
export const { setScheduleDetails } = ganttDetailsSlice.actions;

const { updateTaskData: _updateTaskDataRaw } = taskDataSlice.actions;
const { setScheduleSliderTime: _setScheduleSliderTimeRaw } = sliderTimerSlice.actions;

/**
 * Object-payload action creators are wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 */
export const updateTaskData = <T extends Exact<TaskStatusUpdatePayload, T>>(payload: T) => _updateTaskDataRaw(payload);
export const setScheduleSliderTime = <T extends Exact<Partial<SliderTimerState>, T>>(payload: T) =>
  _setScheduleSliderTimeRaw(payload);
export const setSimulationDisplaySettings = <T extends Exact<SimulationDisplaySettingsPayload, T>>(payload: T) =>
  _setSimulationDisplaySettingsRaw(payload);

export const TaskData = taskDataSlice.reducer;
export const TaskDetails = taskDetailsSlice.reducer;
export const GanttDetails = ganttDetailsSlice.reducer;
export const scheduleError = scheduleErrorSlice.reducer;
export const TaskTypes = taskTypesSlice.reducer;
export const sliderTimer = sliderTimerSlice.reducer;
export const selectionSetTaskElements = selectionSetElementsSlice.reducer;
export const simulationMetrics = simulationMetricsSlice.reducer;

export default {
  TaskData,
  TaskDetails,
  GanttDetails,
  scheduleError,
  TaskTypes,
  sliderTimer,
  selectionSetTaskElements,
  simulationMetrics,
};
